import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  CloudUpload as UploadIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Refresh as RetryIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { uploadDirectService, DirectUploadProgress } from '../../services/upload-direct.service';
import {
  UPLOAD_ERROR_MESSAGES,
  isRetryable,
  UploadError,
  type UploadErrorCode,
} from '../../services/upload-errors';
import { getFileIcon } from '../../utils/fileIconUtils';

interface FileUploadZoneProps {
  onDrop: (files: File[]) => void;
  acceptedTypes?: string[];
  maxSize?: string;
  multiple?: boolean;
  onUploadComplete?: (uploadedFiles: DirectUploadProgress[]) => void;
  organizationId: string;
  teamId: string;
  metadata?: {
    title?: string;
    description?: string;
    content_type?: string;
    tag_group_values?: Record<string, string[]>;
  };
  affirmationStatus?: {
    hasAffirmed: boolean;
    affirmationDate?: string;
    termsVersion?: string;
  };
  useAiTitle?: boolean;
  useAiDescription?: boolean;
  aiTagMode?: 'off' | 'replace' | 'merge';
}

interface FileUploadItem extends DirectUploadProgress {
  file: File;
  abortController?: AbortController;
  errorCode?: UploadErrorCode;
}

const getFileIconForUpload = (file: File) => {
  return getFileIcon(file.type, file.name);
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'K', 'M', 'G'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(1) + sizes[i];
};

const formatSpeed = (bytesPerSecond: number): string => {
  return `${formatFileSize(bytesPerSecond)}/s`;
};

const getStatusLabel = (status: string): string => {
  // Handle both string and numeric status values
  // UploadStatus enum: 0=UNSPECIFIED, 1=UPLOADING, 2=PAUSED, 3=COMPLETED, 4=FAILED, 5=CANCELLED
  switch (status) {
    case 'unspecified':
    case '0':
      return 'Initializing';
    case 'uploading':
    case '1':
      return 'Uploading';
    // Note: Direct upload service doesn't support pause functionality
    case '2':
      return 'Paused';
    case 'completed':
    case '3':
      return 'Completed';
    case 'failed':
    case 'error':
    case '4':
      return 'Failed';
    case 'cancelled':
    case '5':
      return 'Cancelled';
    default:
      return status;
  }
};

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onDrop,
  acceptedTypes = [], // All file types are now accepted
  maxSize = '10GB',
  multiple = true,
  onUploadComplete,
  organizationId,
  teamId,
  metadata,
  affirmationStatus,
  useAiTitle,
  useAiDescription,
  aiTagMode,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<FileUploadItem[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle drag events
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, []);

  // Handle file input
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    handleFiles(files);
    // Reset the input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Process selected files
  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return;

    // Validate file types and sizes
    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach(file => {
      // File type validation removed - all file types are now accepted

      // Check file size (convert maxSize string to bytes)
      const maxSizeBytes = parseFloat(maxSize) * (maxSize.includes('GB') ? 1024 * 1024 * 1024 : 1024 * 1024);
      if (file.size > maxSizeBytes) {
        errors.push(`${file.name}: File too large (max ${maxSize})`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      // Show error dialog or notification
      console.error('File validation errors:', errors);
    }

    if (validFiles.length > 0) {
      onDrop(validFiles);
      await startUploads(validFiles);
    }
  };

  // Start upload process with real API calls
  const startUploads = async (files: File[]) => {
    setShowUploadDialog(true);

    const newUploads: FileUploadItem[] = files.map(file => ({
      file,
      fileId: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      fileName: file.name,
      progress: 0,
      status: 'uploading' as const,
      uploadedBytes: 0,
      totalBytes: file.size,
      speed: 0,
    }));

    setUploadQueue(newUploads);

    // Start uploads concurrently (but limit concurrency to avoid overwhelming the server)
    const maxConcurrentUploads = 3;
    const uploadPromises: Promise<void>[] = [];

    for (let i = 0; i < files.length; i += maxConcurrentUploads) {
      const batch = files.slice(i, i + maxConcurrentUploads);
      const batchPromises = batch.map((file, batchIndex) => {
        const uploadIndex = i + batchIndex;
        return startRealUpload(file, newUploads[uploadIndex]);
      });
      uploadPromises.push(...batchPromises);
      
      // Wait for this batch to complete before starting the next
      await Promise.allSettled(batchPromises);
    }
  };

  // Start real upload for a single file
  const startRealUpload = async (file: File, uploadItem: FileUploadItem) => {
    // Validate team ID is provided
    if (!teamId || teamId.trim() === '') {
      setUploadQueue(prev => prev.map(item => {
        if (item.fileId !== uploadItem.fileId) return item;
        return {
          ...item,
          status: 'error',
          error: 'Team ID is required for file uploads. Please select a team before uploading.',
        };
      }));
      return;
    }

    // Create AbortController for this upload
    const abortController = new AbortController();

    // Store abort controller in upload queue
    setUploadQueue(prev => prev.map(item => {
      if (item.fileId !== uploadItem.fileId) return item;
      return { ...item, abortController };
    }));

    // Progress callback
    const handleProgress = (progress: DirectUploadProgress) => {
      setUploadQueue(prev => prev.map(item => {
        if (item.fileId !== uploadItem.fileId) return item;
        return {
          ...item,
          progress: progress.progress,
          uploadedBytes: progress.uploadedBytes,
          speed: progress.speed,
          status: progress.status,
          contentId: progress.contentId, // Store content ID for metadata updates
        };
      }));
    };

    try {
      // Create uploader affirmation from saved profile affirmation
      const uploaderAffirmation = affirmationStatus?.hasAffirmed && affirmationStatus.affirmationDate ? {
        confirmed: true,
        timestamp: new Date(affirmationStatus.affirmationDate),
        termsVersion: affirmationStatus.termsVersion || '2025-01-17',
        policyVersion: '1.0',
      } : undefined;

      // Direct R2 upload with affirmation data and abort controller
      await uploadDirectService.uploadFile(
        file,
        organizationId,
        teamId,
        metadata,
        handleProgress,
        uploaderAffirmation,
        abortController,
        useAiTitle,
        aiTagMode,
        useAiDescription,
      );

    } catch (error: unknown) {
      console.error('Upload failed:', error);
      const code = error instanceof UploadError ? error.code : undefined;
      const message = error instanceof Error ? error.message : 'Unknown error';
      setUploadQueue(prev => prev.map(item => {
        if (item.fileId !== uploadItem.fileId) return item;
        return {
          ...item,
          status: 'error',
          error: `Upload failed: ${message}`,
          errorCode: code,
        };
      }));
    }
  };

  // Handle upload actions
  const handleUploadAction = async (fileId: string, action: 'pause' | 'resume' | 'cancel' | 'retry') => {
    const uploadItem = uploadQueue.find(item => item.fileId === fileId);
    if (!uploadItem) return;

    switch (action) {
      case 'pause':
        // Note: Real pause/resume would require more complex state management
        // Direct upload cannot be paused - this is a no-op
        break;
        
      case 'resume':
        // For real implementation, this would need to track chunk state
        setUploadQueue(prev => prev.map(item => 
          item.fileId === fileId ? { ...item, status: 'uploading' } : item
        ));
        break;
        
      case 'cancel':
        // Abort the upload using the AbortController
        if (uploadItem.abortController) {
          uploadItem.abortController.abort();
        }
        setUploadQueue(prev => prev.map(item =>
          item.fileId === fileId ? { ...item, status: 'error', error: 'Upload cancelled by user', errorCode: 'UPLOAD_CANCELLED' } : item
        ));
        break;
        
      case 'retry':
        // Reset upload state and restart
        setUploadQueue(prev => prev.map(item =>
          item.fileId === fileId
            ? { ...item, status: 'uploading', progress: 0, uploadedBytes: 0, error: undefined, errorCode: undefined }
            : item
        ));
        // Restart the upload
        await startRealUpload(uploadItem.file, uploadItem);
        break;
        
      default:
        break;
    }
  };

  // Handle both string and numeric status values
  // UploadStatus enum: 0=UNSPECIFIED, 1=UPLOADING, 2=PAUSED, 3=COMPLETED, 4=FAILED, 5=CANCELLED
  const isCompleted = (status: string) => status === 'completed' || status === '3';
  const isError = (status: string) => status === 'failed' || status === 'error' || status === '4';
  const isActive = (status: string) => ['uploading', 'processing'].includes(status);
  
  const completedUploads = uploadQueue.filter(item => isCompleted(item.status));
  const activeUploads = uploadQueue.filter(item => isActive(item.status));
  const hasErrors = uploadQueue.some(item => isError(item.status));

  // Auto-transition when all uploads are completed
  useEffect(() => {
    if (uploadQueue.length > 0 && completedUploads.length === uploadQueue.length && !hasErrors) {
      // All uploads completed successfully, auto-transition to metadata step
      setTimeout(() => {
        onUploadComplete?.(completedUploads);
        setShowUploadDialog(false);
      }, 1000); // Wait 1 second to show the success state
    }
  }, [uploadQueue.length, completedUploads.length, hasErrors, onUploadComplete]);

  return (
    <>
      {/* Drop Zone */}
      <Paper
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          p: 4,
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : 'grey.300',
          backgroundColor: isDragOver ? 'primary.50' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          textAlign: 'center',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'primary.50',
          },
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept="*/*"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        <UploadIcon
          sx={{
            fontSize: 48,
            color: isDragOver ? 'primary.main' : 'grey.400',
            mb: 2,
          }}
        />

        <Typography variant="h6" gutterBottom>
          {isDragOver ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Supports: {acceptedTypes.join(', ')} • Max size: {maxSize}
        </Typography>

        <Button variant="outlined" size="large">
          Browse Files
        </Button>
      </Paper>

      {/* Upload Queue Status */}
      {uploadQueue.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1">
              Upload Queue ({completedUploads.length}/{uploadQueue.length} completed)
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowUploadDialog(true)}
            >
              View Details
            </Button>
          </Box>

          {activeUploads.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Overall Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  (uploadQueue.reduce((sum, item) => sum + item.progress, 0) / uploadQueue.length)
                }
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}

        </Box>
      )}

      {/* Upload Progress Dialog */}
      <Dialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Upload Progress ({completedUploads.length}/{uploadQueue.length} completed)
        </DialogTitle>
        <DialogContent>
          <List>
            {uploadQueue.map((item) => (
              <ListItem
                key={item.fileId}
                divider
                sx={{
                  alignItems: 'flex-start',
                  paddingRight: '80px' // Reserve space for action buttons
                }}
              >
                <ListItemIcon>
                  {isCompleted(item.status) ? (
                    <SuccessIcon color="success" />
                  ) : isError(item.status) ? (
                    <ErrorIcon color="error" />
                  ) : (
                    getFileIconForUpload(item.file)
                  )}
                </ListItemIcon>

                <ListItemText
                  disableTypography
                  sx={{ paddingRight: 0 }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body1" noWrap>
                        {item.fileName}
                      </Typography>
                      <Chip
                        size="small"
                        label={getStatusLabel(item.status)}
                        color={
                          isCompleted(item.status) ? 'success' :
                          isError(item.status) ? 'error' :
                          'primary'
                        }
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">
                          {formatFileSize(item.uploadedBytes)} / {formatFileSize(item.totalBytes)}
                        </Typography>
                        {item.speed > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {formatSpeed(item.speed)}
                          </Typography>
                        )}
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={item.progress}
                        sx={{ height: 6, borderRadius: 3 }}
                        color={
                          isCompleted(item.status) ? 'success' :
                          isError(item.status) ? 'error' : 'primary'
                        }
                      />
                      {isError(item.status) && item.error && (
                        <Box sx={{ mt: 1 }}>
                          <Alert
                            severity={item.errorCode === 'UPLOAD_CANCELLED' ? 'info' : 'error'}
                            action={
                              item.errorCode && isRetryable(item.errorCode) ? (
                                <Button
                                  color="inherit"
                                  size="small"
                                  onClick={() => handleUploadAction(item.fileId, 'retry')}
                                >
                                  {item.errorCode === 'SIGNATURE_MISMATCH' || item.errorCode === 'CORS_REJECTED'
                                    ? 'Re-select & retry'
                                    : 'Retry'}
                                </Button>
                              ) : undefined
                            }
                            sx={{ fontSize: 13 }}
                          >
                            {item.errorCode && UPLOAD_ERROR_MESSAGES[item.errorCode]
                              ? UPLOAD_ERROR_MESSAGES[item.errorCode]
                              : item.error}
                          </Alert>
                        </Box>
                      )}
                    </Box>
                  }
                />

                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {item.status === 'uploading' && (
                      <IconButton
                        size="small"
                        onClick={() => handleUploadAction(item.fileId, 'pause')}
                      >
                        <PauseIcon />
                      </IconButton>
                    )}
                    
                    {false && (
                      <IconButton
                        size="small"
                        onClick={() => handleUploadAction(item.fileId, 'resume')}
                      >
                        <ResumeIcon />
                      </IconButton>
                    )}
                    
                    {item.status === 'error' && (
                      <IconButton
                        size="small"
                        onClick={() => handleUploadAction(item.fileId, 'retry')}
                      >
                        <RetryIcon />
                      </IconButton>
                    )}
                    
                    {item.status !== 'completed' && (
                      <IconButton
                        size="small"
                        onClick={() => handleUploadAction(item.fileId, 'cancel')}
                      >
                        <CancelIcon />
                      </IconButton>
                    )}
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadDialog(false)}>
            Close
          </Button>
          {completedUploads.length > 0 && (
            <Button
              variant="contained"
              onClick={() => {
                onUploadComplete?.(completedUploads);
                setShowUploadDialog(false);
              }}
            >
              Continue with {completedUploads.length} file{completedUploads.length > 1 ? 's' : ''}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileUploadZone;
